import React, {useEffect, useMemo, useState, useCallback, useRef} from 'react';
import {View, StyleSheet, Share, Linking, Platform, TouchableOpacity} from 'react-native';
import {Menu, Snackbar} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {WebView} from 'react-native-webview';
import RNPrint from 'react-native-print';
import Clipboard from '@react-native-clipboard/clipboard';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useAuthStore} from '../../store/useAuthStore';
import {useConfigStore} from '../../store/useConfigStore';
import {generateBillHtml, generateBillSummaryText} from '../../utils/billTemplate';
import GradientHeader from '../../components/common/GradientHeader';
import {colors} from '../../theme';

const OrderBillScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const {order} = route.params;

  const {user} = useAuthStore();
  const {fetchByType, getProperty, properties} = useConfigStore();
  const [menuVisible, setMenuVisible] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    fetchByType('BILL');
    fetchByType('TAX');
  }, [fetchByType]);

  const tenant = user?.tenant;

  const billSettings = useMemo(() => ({
    termsHeader: getProperty('BILL', 'BILL_TERMS_HEADER'),
    termsContent: getProperty('BILL', 'BILL_TERMS'),
    footerMessage: getProperty('BILL', 'BILL_FOOTER'),
    taxName: getProperty('TAX', 'TAX_NAME'),
    taxNumber: getProperty('TAX', 'TAX_NUMBER'),
  }), [getProperty, properties]);

  const billHtml = useMemo(
    () => generateBillHtml(order, tenant, billSettings),
    [order, tenant, billSettings],
  );

  const summaryText = useMemo(
    () => generateBillSummaryText(order, tenant),
    [order, tenant],
  );

  const customerPhone = order.customerPhone || '';

  const handleCopy = useCallback(() => {
    setMenuVisible(false);
    Clipboard.setString(summaryText);
    setSnackbar(t('bill.copiedToClipboard'));
  }, [summaryText, t]);

  const handlePrint = useCallback(async () => {
    setMenuVisible(false);
    try {
      await RNPrint.print({html: billHtml});
    } catch (e) {
      if (e.message !== 'User cancelled') {
        setSnackbar(t('common.error'));
      }
    }
  }, [billHtml, t]);

  const handleShare = useCallback(async () => {
    setMenuVisible(false);
    try {
      await Share.share({message: summaryText});
    } catch (_) {}
  }, [summaryText]);

  const handleSms = useCallback(() => {
    setMenuVisible(false);
    const encoded = encodeURIComponent(summaryText);
    const url = Platform.OS === 'ios'
      ? `sms:${customerPhone}&body=${encoded}`
      : `sms:${customerPhone}?body=${encoded}`;
    Linking.openURL(url).catch(() => setSnackbar(t('common.error')));
  }, [customerPhone, summaryText, t]);

  const handleWhatsApp = useCallback(() => {
    setMenuVisible(false);
    const phone = customerPhone.replace(/[^0-9]/g, '');
    const whatsappUrl = phone
      ? `whatsapp://send?phone=${phone}&text=${encodeURIComponent(summaryText)}`
      : `whatsapp://send?text=${encodeURIComponent(summaryText)}`;
    Linking.openURL(whatsappUrl).catch(() =>
      setSnackbar(t('common.error')),
    );
  }, [customerPhone, summaryText, t]);

  return (
    <View style={styles.container}>
      <GradientHeader title={t('bill.title')} onBack={() => navigation.goBack()}>
        <Menu
          visible={menuVisible}
          onDismiss={() => setMenuVisible(false)}
          anchor={
            <TouchableOpacity onPress={() => setMenuVisible(true)} style={{width: 48, height: 48, justifyContent: 'center', alignItems: 'center'}}>
              <Icon name="share-variant" size={24} color={colors.primary} />
            </TouchableOpacity>
          }>
          <Menu.Item
            leadingIcon="content-copy"
            title={t('bill.copyToClipboard')}
            onPress={handleCopy}
          />
          <Menu.Item
            leadingIcon="printer"
            title={t('bill.print')}
            onPress={handlePrint}
          />
          <Menu.Item
            leadingIcon="share"
            title={t('bill.shareBill')}
            onPress={handleShare}
          />
          <Menu.Item
            leadingIcon="message-text"
            title={t('bill.sendSms')}
            onPress={handleSms}
          />
          <Menu.Item
            leadingIcon="whatsapp"
            title={t('bill.sendWhatsApp')}
            onPress={handleWhatsApp}
          />
        </Menu>
      </GradientHeader>

      <WebView
        source={{html: billHtml}}
        style={styles.webview}
        originWhitelist={['*']}
        scrollEnabled={true}
      />

      <Snackbar
        visible={!!snackbar}
        onDismiss={() => setSnackbar('')}
        duration={2000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  webview: {flex: 1},
});

export default OrderBillScreen;
